import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1D2951] text-white p-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center">
            <ShoppingCart size={20} />
          </div>
          <div>
            <div className="font-semibold tracking-tight">PMS</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-300">
              Purchase Management System
            </div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-3">
            Industrial · Procurement · Audit
          </div>
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            Run procurement with the precision of a control room.
          </h1>
          <p className="mt-4 text-slate-300 text-sm leading-relaxed">
            Indent in seconds, approve in clicks, generate compliant Purchase Orders
            with tax-ready breakdowns and printable templates.
          </p>
        </div>

        <div className="text-[11px] font-mono text-slate-400">v1.0 · build.industrial</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-slate-50">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm bg-white border border-slate-200 rounded-lg shadow-sm p-8"
          data-testid="login-form"
          autoComplete="off"
        >
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">
            Sign in
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            Access your workspace
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            Use your company credentials to continue.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Username
              </label>
              <div className="relative mt-1">
                <Mail size={14} className="absolute left-3 top-3 text-slate-400" />
                <input
                  data-testid="login-email"
                  type="text"
                  autoCapitalize="off"
                  autoCorrect="off"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Password
              </label>
              <div className="relative mt-1">
                <Lock size={14} className="absolute left-3 top-3 text-slate-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-6 w-full bg-[#1D2951] hover:bg-[#2B3A67] text-white py-2.5 rounded-md text-sm font-semibold tracking-wide transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
